import React from 'react'
import { useNavigate } from 'react-router'
import { CreditApplication } from '_gql'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators/useTrackCollaboratorEvent'
import useGetIsLoggedInAsAuthSigner from '@sputnik/hooks/useGetIsLoggedInAsAuthSigner'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { DSA_VERSION_TYPES } from '@sputnik/resources/constants'
import { PATHNAME_CONSTS, PRODUCT_CONST_STRINGS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logInfo } from '@sputnik/utils/loggerUtils'

interface ReviewMigrationBannerProps extends Pick<
  GetCreditApplicationDetails_creditApplication_CreditApplication,
  'authorizedSigner'
> {
  dsaVersion: CreditApplication['dsaVersion']
  salesforceCreditId: string
}

// this banner shows for needing to review and resubmit a migration application
const ReviewMigrationBanner = ({ salesforceCreditId, dsaVersion, authorizedSigner }: ReviewMigrationBannerProps) => {
  const navigate = useNavigate()
  const { track } = useWebTracking()
  const { isLoggedInUserAuthorizedSigner } = useGetIsLoggedInAsAuthSigner({
    authSignerEmail: authorizedSigner?.email || null,
  })

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  useDidMount(() => {
    trackCollaboratorEvent(
      'se_signup__application_dashboard_action_needed_review_application_migration__presented',
      (role) => ({
        person_type: role,
      }),
    )
  })

  const migrationType =
    dsaVersion === DSA_VERSION_TYPES.CRB_MC_B_O || dsaVersion === DSA_VERSION_TYPES.CRB_MC_J_S ? 'Mastercard' : 'Visa'

  const onReviewApplicationClick = () => {
    const navigatePath = `/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`

    logInfo('onReviewApplicationClick', {
      attributes: {
        creditAppId: salesforceCreditId,
        result: `Clicked review ${migrationType} application link from banner and navigated to ${navigatePath}`,
      },
      eventName: 'onReviewApplicationClick',
    })

    track('se_signup___application_dashboard_action_needed_review_and_resubmit_your_application__fired')

    navigate(navigatePath)
  }

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Informative}
        subtext={
          <FormattedMessage
            defaultMessage='As requested, we’re migrating your {BILL_SPEND_AND_EXPENSE} account to {migrationType}. {link}'
            id='sputnik.ReviewMigrationBanner__dezVS5'
            values={{
              BILL_SPEND_AND_EXPENSE: PRODUCT_CONST_STRINGS.BILL_SPEND_AND_EXPENSE,
              migrationType: migrationType,
              link: (
                <TextLink
                  size={TextLinkSizes.SMALL}
                  onClick={onReviewApplicationClick}
                >
                  <FormattedMessage
                    defaultMessage='{isLoggedInUserAuthorizedSigner, select,
                      true {Review and re-submit your application}
                      other {Review and send to signer}
                    }'
                    id='sputnik.ReviewMigrationBanner__h8npd+'
                    values={{ isLoggedInUserAuthorizedSigner }}
                  />
                </TextLink>
              ),
            }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Review your {migrationType} application'
            id='sputnik.ReviewMigrationBanner__1JRfgv'
            values={{
              migrationType: migrationType,
            }}
          />
        }
      />
    </div>
  )
}

export default ReviewMigrationBanner
